import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { mkdtemp, readFile, writeFile, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Launcher for GitHub Copilot CLI, with support for the legacy gh extension */
export class CopilotLauncher implements ProviderLauncher {
  readonly name = 'copilot'
  readonly displayName = 'GitHub Copilot CLI'
  readonly pricingHint = '💰 Payant (abonnement GitHub Copilot requis, 10 $/mois)'

  async isInstalled(): Promise<boolean> {
    return (await this.getBinaryName()) !== null
  }

  async getVersion(): Promise<string | null> {
    const binary = await this.getBinaryName()
    if (!binary) return null
    try {
      const { stdout } = await execFileAsync(
        binary,
        binary === 'copilot' ? ['--version'] : ['copilot', '--version'],
      )
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    // Write AGENTS.md in a temp dir so candidate can't read it.
    // Tell the tool where the real workspace is via the system prompt.
    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-copilot-'))
    await writeFile(join(agentDir, 'AGENTS.md'), `Work directory: ${workdir}\n\n${systemPrompt}`)

    const binaryName = (await this.getBinaryName()) ?? 'copilot'
    const args: string[] = binaryName === 'copilot' ? [] : ['copilot', 'suggest']

    const recorder = new SessionRecorder()

    const ptyProc = getPty().spawn(await resolveBinaryOrFallback(binaryName), args, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string | null> {
    try {
      await execFileAsync('copilot', ['--version'])
      return 'copilot'
    } catch {
      try {
        await execFileAsync('gh', ['copilot', '--version'])
        return 'gh'
      } catch {
        return null
      }
    }
  }
}
